import type { FieldValues, Path, UseFormSetError } from 'react-hook-form'
import { toast } from '@/components/feedback/toast-store'
import { toApiError } from '@/api/errors'
import { tAny } from '@/i18n'

/**
 * Maps a 422 response's violations onto React Hook Form fields; anything not tied to a known
 * field (or any other error) is surfaced as a toast. Returns true when field errors were applied.
 */
export function applyServerErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  fields: readonly string[],
): boolean {
  const apiError = toApiError(error)
  let applied = false
  const leftovers: string[] = []
  for (const v of apiError.violations) {
    // "guardians[0].name" → "guardians.0.name" (RHF path syntax)
    const path = v.field.replace(/\[(\d+)\]/g, '.$1')
    const root = path.split('.')[0] ?? path
    if (fields.includes(root)) {
      setError(path as Path<T>, { type: 'server', message: v.message }, { shouldFocus: !applied })
      applied = true
    } else {
      leftovers.push(v.message)
    }
  }
  if (!applied || leftovers.length) {
    toast.error(
      apiError.status === 422 ? tAny('Please check the form') : tAny('Could not save'),
      leftovers[0] ?? apiError.message,
    )
  }
  return applied
}
